/*
  Sabry theme core: vanilla web components, no dependencies.
  Loaded on every page. Page-specific behaviour lives in product.js and collection.js.
  Direction aware: anything that moves along the inline axis reads the computed direction.
*/
(() => {
  'use strict';

  const Theme = (window.Theme = window.Theme || {});
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  const debounce = (fn, wait = 250) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), wait);
    };
  };

  /* ---------- Money ---------- */
  function formatMoney(cents, format = Theme.moneyFormat || '{{amount}}') {
    const value = Number(cents) / 100;
    const fmt = (n, decimals, thousands = ',', decimal = '.') => {
      const [int, dec] = n.toFixed(decimals).split('.');
      const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return dec ? grouped + decimal + dec : grouped;
    };
    const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!match) return format;
    let amount;
    switch (match[1]) {
      case 'amount_no_decimals':
        amount = fmt(value, 0);
        break;
      case 'amount_with_comma_separator':
        amount = fmt(value, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        amount = fmt(value, 0, '.');
        break;
      case 'amount_with_apostrophe_separator':
        amount = fmt(value, 2, "'");
        break;
      default:
        amount = fmt(value, 2);
    }
    // Match Liquid's money_without_trailing_zeros: whole pounds never show ".00".
    amount = amount.replace(/[.,]00$/, '');
    return format.replace(match[0], amount);
  }
  Theme.formatMoney = formatMoney;

  /* ---------- Toast ---------- */
  function toast(message) {
    let el = document.querySelector('.toast');
    if (!el) {
      el = document.createElement('div');
      el.className = 'toast';
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      document.body.append(el);
    }
    el.textContent = message;
    el.classList.add('is-visible');
    clearTimeout(el.hideTimer);
    el.hideTimer = setTimeout(() => el.classList.remove('is-visible'), 3800);
  }
  Theme.toast = toast;

  /* ---------- Scroll lock (counted, so nested drawers behave) ---------- */
  let locks = 0;
  const lockScroll = () => {
    if (locks++ === 0) document.body.classList.add('scroll-lock');
  };
  const unlockScroll = () => {
    if (locks > 0 && --locks === 0) document.body.classList.remove('scroll-lock');
  };

  /* ---------- Drawer panel (menu and cart) ---------- */
  class DrawerPanel extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-drawer-close]')) this.close();
      });
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.close();
        if (event.key === 'Tab') this.trapFocus(event);
      });
    }

    get panel() {
      return this.querySelector('.drawer');
    }

    get isOpen() {
      return this.classList.contains('is-open');
    }

    setExpanded(value) {
      document
        .querySelectorAll(`[data-drawer-toggle="${this.id}"]`)
        .forEach((btn) => btn.setAttribute('aria-expanded', String(value)));
    }

    open(trigger) {
      if (this.isOpen) return;
      this.trigger = trigger || document.activeElement;
      this.classList.add('is-open');
      lockScroll();
      this.setExpanded(true);
      requestAnimationFrame(() => {
        const target = this.panel.querySelector('[data-autofocus]') || this.panel;
        target.focus({ preventScroll: true });
      });
    }

    close() {
      if (!this.isOpen) return;
      this.classList.remove('is-open');
      unlockScroll();
      this.setExpanded(false);
      if (this.trigger && typeof this.trigger.focus === 'function') this.trigger.focus({ preventScroll: true });
    }

    trapFocus(event) {
      const items = [...this.panel.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }
  customElements.define('drawer-panel', DrawerPanel);

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-drawer-toggle]');
    if (!toggle) return;
    const drawer = document.getElementById(toggle.dataset.drawerToggle);
    if (!drawer || typeof drawer.open !== 'function') return;
    event.preventDefault();
    drawer.isOpen ? drawer.close() : drawer.open(toggle);
  });

  /* ---------- Sticky header ---------- */
  class StickyHeader extends HTMLElement {
    connectedCallback() {
      const setOffset = () =>
        document.documentElement.style.setProperty('--header-offset', `${this.offsetHeight}px`);
      setOffset();
      new ResizeObserver(setOffset).observe(this);

      if (!this.hasAttribute('data-sticky')) return;
      const onScroll = () => this.classList.toggle('is-scrolled', window.scrollY > 4);
      onScroll();
      window.addEventListener('scroll', onScroll, { passive: true });
    }
  }
  customElements.define('sticky-header', StickyHeader);

  /* ---------- Announcement bar: rotates on mobile, shows all on desktop ---------- */
  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.items = [...this.querySelectorAll('.announcement__item')];
      this.index = 0;
      this.desktop = window.matchMedia('(min-width: 990px)');
      this.sync();
      this.desktop.addEventListener('change', () => this.sync());
      if (this.items.length < 2 || reducedMotion.matches) return;

      const pause = () => (this.paused = true);
      const resume = () => (this.paused = false);
      this.addEventListener('mouseenter', pause);
      this.addEventListener('mouseleave', resume);
      this.addEventListener('focusin', pause);
      this.addEventListener('focusout', resume);
      this.timer = setInterval(() => this.advance(), 4500);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    sync() {
      this.items.forEach((item, i) => {
        if (this.desktop.matches || i === this.index) item.removeAttribute('aria-hidden');
        else item.setAttribute('aria-hidden', 'true');
      });
    }

    advance() {
      if (this.paused || this.desktop.matches || document.hidden) return;
      this.index = (this.index + 1) % this.items.length;
      this.sync();
    }
  }
  customElements.define('announcement-bar', AnnouncementBar);

  /* ---------- Desktop nav dropdowns: one open at a time ---------- */
  document.addEventListener(
    'toggle',
    (event) => {
      const details = event.target;
      if (!(details instanceof HTMLDetailsElement) || !details.classList.contains('nav-dropdown') || !details.open)
        return;
      document.querySelectorAll('details.nav-dropdown[open]').forEach((other) => {
        if (other !== details) other.open = false;
      });
    },
    true
  );
  document.addEventListener('click', (event) => {
    document.querySelectorAll('details.nav-dropdown[open]').forEach((details) => {
      if (!details.contains(event.target)) details.open = false;
    });
  });
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    document.querySelectorAll('details.nav-dropdown[open]').forEach((details) => {
      details.open = false;
      details.querySelector('summary')?.focus();
    });
  });

  /* ---------- Predictive search ---------- */
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('.predictive');
      if (!this.input || !this.results) return;
      this.active = -1;

      this.input.addEventListener('input', debounce(() => this.search(), 220));
      this.input.addEventListener('focus', () => {
        if (this.input.value.trim() && this.results.innerHTML.trim()) this.show();
      });
      this.input.addEventListener('keydown', (event) => this.onKeydown(event));
      document.addEventListener('click', (event) => {
        if (!this.contains(event.target)) this.hide();
      });
    }

    async search() {
      const q = this.input.value.trim();
      if (!q) {
        this.results.innerHTML = '';
        this.hide();
        return;
      }
      this.controller?.abort();
      this.controller = new AbortController();
      const params = new URLSearchParams({
        q,
        section_id: this.dataset.sectionId || 'predictive-search',
        'resources[type]': 'product,collection,query',
        'resources[limit]': '6',
        'resources[options][unavailable_products]': 'last',
        'resources[options][fields]': 'title,product_type,vendor,variants.title,variants.sku,tag',
      });
      try {
        const res = await fetch(`${Theme.routes.predictiveSearch}?${params}`, { signal: this.controller.signal });
        if (!res.ok) throw new Error(String(res.status));
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const inner = doc.querySelector('#predictive-search-results');
        this.results.innerHTML = inner ? inner.outerHTML : '';
        this.active = -1;
        inner ? this.show() : this.hide();
      } catch (error) {
        if (error.name !== 'AbortError') this.hide();
      }
    }

    get options() {
      return [...this.results.querySelectorAll('[role="option"] > a, .predictive__all')];
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        this.hide();
        return;
      }
      const options = this.options;
      if (event.key === 'Enter' && this.active >= 0 && options[this.active]) {
        event.preventDefault();
        window.location.href = options[this.active].href;
        return;
      }
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      if (!options.length) return;
      event.preventDefault();
      const delta = event.key === 'ArrowDown' ? 1 : -1;
      this.active = (this.active + delta + options.length) % options.length;
      options.forEach((opt, i) => opt.classList.toggle('is-active', i === this.active));
      options[this.active].scrollIntoView({ block: 'nearest' });
      const optionEl = options[this.active].closest('[role="option"]');
      this.input.setAttribute('aria-activedescendant', optionEl ? optionEl.id : '');
    }

    show() {
      this.results.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    }

    hide() {
      this.results.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
    }
  }
  customElements.define('predictive-search', PredictiveSearch);

  /* ---------- Cart ---------- */
  const Cart = {
    sectionIds() {
      return [...new Set([...document.querySelectorAll('[data-cart-section]')].map((el) => el.dataset.cartSection))];
    },

    render(sections) {
      if (!sections) return;
      Object.entries(sections).forEach(([id, html]) => {
        if (!html) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector(`[data-cart-section="${id}"]`);
        if (!fresh) return;
        document.querySelectorAll(`[data-cart-section="${id}"]`).forEach((target) => {
          target.innerHTML = fresh.innerHTML;
        });
      });
    },

    setCount(count) {
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = count;
        el.hidden = count === 0;
      });
    },

    async refreshCount() {
      const res = await fetch(`${Theme.routes.cart}.js`, { headers: { Accept: 'application/json' } });
      if (res.ok) this.setCount((await res.json()).item_count);
    },

    errorMessage(data) {
      return (data && (data.description || data.message)) || Theme.strings.cartError;
    },
  };
  Theme.Cart = Cart;

  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.form.addEventListener('submit', (event) => this.onSubmit(event));
    }

    async onSubmit(event) {
      if (Theme.cartType === 'page') return;
      event.preventDefault();
      const button = this.form.querySelector('[type="submit"]');
      const errorEl = this.querySelector('[data-form-error]');
      if (button?.getAttribute('aria-disabled') === 'true') return;

      button?.classList.add('is-loading');
      button?.setAttribute('aria-disabled', 'true');
      if (errorEl) errorEl.hidden = true;

      const body = new FormData(this.form);
      const sections = Cart.sectionIds();
      if (sections.length) {
        body.append('sections', sections.join(','));
        body.append('sections_url', window.location.pathname);
      }

      try {
        const res = await fetch(`${Theme.routes.cartAdd}.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body,
        });
        const data = await res.json();
        if (!res.ok || data.status) throw data;

        Cart.render(data.sections);
        await Cart.refreshCount();
        document.dispatchEvent(new CustomEvent('cart:added', { detail: data }));
        // The cart page was just re-rendered in place, so no drawer on top of it.
        if (document.body.classList.contains('template-cart')) return;

        const drawer = document.getElementById('CartDrawer');
        if (drawer && typeof drawer.open === 'function') drawer.open(button);
        else window.location.href = Theme.routes.cart;
      } catch (data) {
        const message = Cart.errorMessage(data);
        if (errorEl) {
          errorEl.textContent = message;
          errorEl.hidden = false;
        } else {
          toast(message);
        }
      } finally {
        button?.classList.remove('is-loading');
        button?.removeAttribute('aria-disabled');
      }
    }
  }
  customElements.define('product-form', ProductForm);

  class CartItems extends HTMLElement {
    connectedCallback() {
      const onChange = debounce((input) => this.update(input.dataset.line, input.value), 400);
      this.addEventListener('change', (event) => {
        const input = event.target.closest('[data-line-qty]');
        if (input) onChange(input);
      });
      this.addEventListener('click', (event) => {
        const remove = event.target.closest('[data-line-remove]');
        if (!remove) return;
        event.preventDefault();
        this.update(remove.dataset.line, 0);
      });
    }

    async update(line, quantity) {
      this.classList.add('is-busy');
      try {
        const res = await fetch(`${Theme.routes.cartChange}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            line: Number(line),
            quantity: Math.max(0, Number(quantity) || 0),
            sections: Cart.sectionIds(),
            sections_url: window.location.pathname,
          }),
        });
        const data = await res.json();
        if (!res.ok || data.status) throw data;
        Cart.render(data.sections);
        Cart.setCount(data.item_count);
      } catch (data) {
        toast(Cart.errorMessage(data));
        await Cart.refreshCount();
      } finally {
        this.classList.remove('is-busy');
      }
    }
  }
  customElements.define('cart-items', CartItems);

  /* ---------- Quantity input ---------- */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.addEventListener('click', (event) => {
        const btn = event.target.closest('[data-qty-step]');
        if (!btn || !this.input) return;
        event.preventDefault();
        const min = Number(this.input.min || 0);
        const max = this.input.max ? Number(this.input.max) : Infinity;
        const current = Number(this.input.value) || 0;
        const next = Math.min(max, Math.max(min, current + Number(btn.dataset.qtyStep)));
        if (next === current) return;
        this.input.value = next;
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
  }
  customElements.define('quantity-input', QuantityInput);

  /* ---------- Horizontal slider: native scroll snap + direction-aware buttons ---------- */
  class SliderComponent extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.slider__track');
      if (!this.track) return;
      this.viewport = this.querySelector('.slider__viewport');
      this.prevBtn = this.querySelector('[data-slider-prev]');
      this.nextBtn = this.querySelector('[data-slider-next]');
      this.prevBtn?.addEventListener('click', () => this.go(-1));
      this.nextBtn?.addEventListener('click', () => this.go(1));
      this.track.addEventListener('scroll', debounce(() => this.update(), 60), { passive: true });
      new ResizeObserver(() => this.update()).observe(this.track);
      this.update();
    }

    pageWidth() {
      const slide = this.track.querySelector('.slider__slide');
      if (!slide) return this.track.clientWidth;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const unit = slide.getBoundingClientRect().width + gap;
      return unit * Math.max(1, Math.floor((this.track.clientWidth + gap) / unit));
    }

    // dir +1 moves toward the inline end. In RTL the inline end is on the left, so the
    // physical scroll offset goes negative.
    go(dir) {
      const sign = getComputedStyle(this.track).direction === 'rtl' ? -1 : 1;
      this.track.scrollBy({ left: dir * sign * this.pageWidth(), behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    }

    update() {
      const max = this.track.scrollWidth - this.track.clientWidth;
      const pos = Math.abs(this.track.scrollLeft);
      if (this.prevBtn) this.prevBtn.disabled = pos <= 2;
      if (this.nextBtn) this.nextBtn.disabled = pos >= max - 2;
      this.placeArrows();
    }

    // Centre the side arrows on the slide images, not on the whole card (title and price
    // below would pull them too low).
    placeArrows() {
      if (!this.viewport) return;
      const media = this.track.querySelector('.slider__slide .media');
      if (!media) return;
      const top = media.getBoundingClientRect().top - this.viewport.getBoundingClientRect().top + media.offsetHeight / 2;
      this.viewport.style.setProperty('--arrow-top', `${Math.round(top)}px`);
    }
  }
  customElements.define('slider-component', SliderComponent);
})();
